import { useState } from 'react';
import { ArrowLeft, ArrowRight, Plus, Trash2, Upload } from 'lucide-react';
import toast from 'react-hot-toast';
import { dictionary } from '../../../utils/dictionary';
import { useAuth } from '../../../context/AuthContext';
import { useGenericImageUpload } from '../../../hooks/useGenericImageUpload';
import { FOLDER_IMAGES, MAX_FILE_SIZE, MAX_IMAGES } from '../../onboarding/const/products';

export interface ConfirmScrapedProductViewProps {
  title: string;
  subtitle?: string;
  name: string;
  description: string;
  images: string[];
  onNameChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  onImagesUpdate: (images: string[]) => void;
  onBack: () => void;
  onConfirm: () => void;
  maxImages?: number;
}

export function ConfirmScrapedProductView({
  title,
  subtitle,
  name,
  description,
  images,
  onNameChange,
  onDescriptionChange,
  onImagesUpdate,
  onBack,
  onConfirm,
  maxImages = MAX_IMAGES,
}: ConfirmScrapedProductViewProps) {
  const { language } = useAuth();
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  const {
    isLoading: isUploadingImages,
    handleImageUpload,
    removeImage: handleRemoveImageFromHook,
  } = useGenericImageUpload({
    folder: FOLDER_IMAGES,
    currentImages: images,
    onImagesUpdate: (newImages) => {
      onImagesUpdate(newImages);
      setSelectedImageIndex(0);
    },
    allowMultiple: maxImages > 1,
    maxImages,
    maxFileSize: MAX_FILE_SIZE,
  });

  const setMainImage = (index: number) => {
    setSelectedImageIndex(index);
  };

  const handleRemoveImage = (index: number) => {
    handleRemoveImageFromHook(index);
    setSelectedImageIndex((prev) =>
      prev >= index && prev > 0 ? prev - 1 : prev === index ? 0 : prev
    );
  };

  const handleConfirm = () => {
    const trimmedName = (name || '').trim();
    const trimmedDescription = (description || '').trim();
    const hasImages = images.length > 0;

    if (!hasImages) {
      toast.error(
        dictionary['product-information-incomplete']?.[language] ?? 'Add at least one image.',
        { id: 'confirm-product-validation' }
      );
      return;
    }
    if (!trimmedName) {
      toast.error(
        dictionary['product-information-incomplete']?.[language] ?? 'Product name is required.',
        { id: 'confirm-product-validation' }
      );
      return;
    }
    if (!trimmedDescription) {
      toast.error(
        dictionary['product-information-incomplete']?.[language] ?? 'Product description is required.',
        { id: 'confirm-product-validation' }
      );
      return;
    }

    onConfirm();
  };

  const mainImageUrl = images[selectedImageIndex] || images[0];
  const changeImageLabel = dictionary['upload-photo']?.[language] ?? 'Cambiar';

  return (
    <div className="flex flex-col items-center w-full max-w-4xl mx-auto pb-8 pt-4 gap-6 flex-1 min-h-0">
      {/* Title only — no subtitle */}
      <h2 className="font-semibold leading-tight text-[28px] text-slate-900 tracking-[-0.5px] text-center">
        {title}
      </h2>

      <div className="w-full flex flex-col md:flex-row gap-6 items-stretch flex-1 min-h-0">
        {/* Image column */}
        <div className="flex flex-col gap-3 w-full md:w-[260px] md:max-w-[260px] shrink-0">
          {/* Main image — natural aspect ratio, not forced square */}
          <div className="w-full h-[260px] rounded-lg overflow-hidden bg-slate-50 border border-slate-200 shadow-sm relative">
            {mainImageUrl ? (
              <div className="w-full h-full flex items-center justify-center">
                <img
                  src={mainImageUrl}
                  alt={name || 'Product'}
                  className="max-w-full max-h-full object-contain"
                />
              </div>
            ) : (
              <label className="w-full h-full flex flex-col items-center justify-center cursor-pointer bg-[#FCFDFD] border-2 border-dashed border-gray-200 hover:border-fluxi transition-colors rounded">
                <input
                  type="file"
                  accept="image/*"
                  multiple={maxImages > 1}
                  onChange={handleImageUpload}
                  disabled={isUploadingImages}
                  className="hidden"
                />
                {isUploadingImages ? (
                  <>
                    <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mb-4 shadow-[0_0_15px_rgba(0,0,0,0.15)]">
                      <div className="w-6 h-6 border-4 border-gray-200 border-t-fluxi rounded-full animate-spin" />
                    </div>
                    <p className="text-sm font-semibold text-gray-600">
                      {dictionary?.['uploading-images']?.[language] ?? 'Cargando imágenes...'}
                    </p>
                  </>
                ) : (
                  <>
                    <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mb-4 shadow-[0_0_15px_rgba(0,0,0,0.15)]">
                      <Upload className="w-6 h-6 text-fluxi" />
                    </div>
                    <p className="text-sm font-semibold text-gray-600">
                      {dictionary['upload-photo']?.[language] ?? 'Subir foto'}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      {dictionary['accepted-formats']?.[language] ?? 'JPG, PNG, WEBP y JPEG'}
                    </p>
                  </>
                )}
              </label>
            )}
          </div>

          {/* Thumbnails — 4-column grid with delete + upload cell */}
          {(
            <div className="grid grid-cols-4 gap-2">
              {images.map((src, index) => (
                <div
                  key={`${index}-${src?.slice(-20)}`}
                  className="relative group"
                >
                  <button
                    type="button"
                    onClick={() => setMainImage(index)}
                    className={`aspect-square w-full rounded overflow-hidden cursor-pointer transition-all ${
                      index === selectedImageIndex
                        ? 'shadow-[0_0_0_1.5px_theme(colors.fluxi.DEFAULT)]'
                        : 'shadow-[0_0_0_1px_#e2e8f0] hover:shadow-[0_0_0_1px_#cbd5e1]'
                    }`}
                  >
                    <img src={src} alt="" className="w-full h-full object-cover" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleRemoveImage(index)}
                    className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-white border border-slate-200 hover:bg-red-50 hover:border-red-300 text-slate-400 hover:text-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10 shadow-sm"
                  >
                    <Trash2 className="size-2.5" />
                  </button>
                </div>
              ))}
              {/* Upload as a grid cell */}
              <label
                className={`aspect-square w-full rounded border-2 border-dashed flex flex-col items-center justify-center transition-colors ${
                  isUploadingImages
                    ? 'cursor-wait border-slate-200 bg-slate-50 text-slate-400'
                    : 'border-slate-300 hover:border-fluxi hover:bg-slate-50 cursor-pointer text-slate-400 hover:text-fluxi'
                }`}
              >
                <input
                  type="file"
                  accept="image/*"
                  multiple={maxImages > 1}
                  onChange={handleImageUpload}
                  disabled={isUploadingImages}
                  className="hidden"
                />
                {isUploadingImages ? (
                  <span className="w-4 h-4 border-2 border-slate-200 border-t-fluxi rounded-full animate-spin block" />
                ) : (
                  <Plus className="size-5" />
                )}
              </label>
            </div>
          )}
        </div>

        {/* Details column */}
        <div className="flex-1 flex flex-col gap-4 w-full h-full">
          <div className="flex flex-col gap-1.5">
            <label className="text-[12px] font-semibold text-slate-400 ml-1">
              {dictionary['product-name']?.[language] ?? 'Nombre'}
            </label>
            <input
              type="text"
              placeholder={dictionary['product-name']?.[language] ?? 'Nombre del producto'}
              value={name}
              onChange={(e) => onNameChange(e.target.value)}
              className="w-full font-semibold text-[14px] text-[#0F172A] leading-tight bg-white border border-slate-200 rounded-[12px] px-3 py-2 focus:border-fluxi focus:shadow-[0_0_0_3px_rgba(62,62,239,0.1)] outline-none transition-all placeholder:text-slate-300"
            />
          </div>
          <div className="flex flex-col gap-1.5 flex-1 min-h-0">
            <label className="text-[12px] font-semibold text-slate-400 ml-1 shrink-0">
              {dictionary['product-description']?.[language] ?? 'Descripción'}
            </label>
            <textarea
              placeholder={dictionary['product-description']?.[language] ?? 'Descripción del producto...'}
              value={description}
              onChange={(e) => onDescriptionChange(e.target.value)}
              className="w-full text-[12px] text-slate-600 leading-relaxed bg-white border border-slate-200 rounded-[12px] px-3 py-2 focus:border-fluxi focus:shadow-[0_0_0_3px_rgba(62,62,239,0.1)] outline-none transition-all resize-none flex-1 min-h-[120px] placeholder:text-slate-300"
            />
          </div>
        </div>
      </div>

      {/* Action buttons — same style as other onboarding steps */}
      <div className="flex justify-between items-center w-full shrink-0">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-2 px-6 py-3 text-gray-600 hover:text-gray-900 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          {dictionary['back']?.[language] ?? 'Volver'}
        </button>
        <button
          type="button"
          onClick={handleConfirm}
          className="flex items-center gap-2 px-7 py-2.5 text-sm text-white rounded-full font-medium bg-fluxi hover:bg-fluxi-dark transition-all"
        >
          {dictionary['confirm-data']?.[language] ?? 'Confirmar datos'}
          <ArrowRight className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}
